import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Calendar, Users, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Satsangs() {
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [paying, setPaying] = useState(false);

  useEffect(() => { api.get("/satsangs").then((r) => setItems(r.data.items)); }, []);

  const book = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    setPaying(true);
    try {
      const { data } = await api.post(`/satsangs/${picked.id}/book`, { ...form, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setPaying(false); }
  };

  return (
    <div data-testid="satsangs-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">॥ सत्संग ॥</div>
      <h1 className="font-serif text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Live Group Satsangs</h1>
      <p className="text-[#63534E] mt-5 text-lg max-w-2xl leading-relaxed">
        Join lectures, bhajan concerts and discourse circles — many devotees in one room with one teacher. Pay a small ticket, receive the live link right after booking.
      </p>

      {items.length === 0 ? (
        <div className="mt-14 bg-[#FFFDF9] sacred-border rounded-3xl p-16 text-center">
          <div className="font-devanagari text-[80px] text-[#D4AF37] leading-none opacity-50">ॐ</div>
          <h3 className="font-serif text-2xl text-[#4A0E17] mt-4">The gathering is forming</h3>
          <p className="text-[#63534E] mt-3 max-w-md mx-auto">No live satsangs are scheduled yet. Follow our newsletter to hear when the next one opens.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/newsletter" data-testid="satsang-empty-follow" className="btn-saffron">Get satsang alerts</Link>
            <Link to="/teachers/apply" data-testid="satsang-empty-host" className="btn-ghost-gold">Host a satsang</Link>
          </div>
        </div>
      ) : (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => (
            <div key={s.id} data-testid={`satsang-${s.id}`} className="bg-[#FFFDF9] sacred-border rounded-2xl p-6 transition-sacred gold-glow">
              <div className="flex items-center gap-2 text-xs text-[#8C6D3B]">
                <span className="capitalize bg-[#F3E5AB]/50 px-2 py-0.5 rounded-full">{s.category}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration_min} min</span>
              </div>
              <h3 className="font-serif text-xl text-[#4A0E17] mt-3 leading-tight">{s.title}</h3>
              <div className="text-sm text-[#63534E] mt-1">by {s.guru_name}</div>
              <p className="text-sm text-[#1F1412] mt-3 line-clamp-3">{s.description}</p>
              <div className="mt-4 flex items-center gap-2 text-sm text-[#63534E]">
                <Calendar className="w-4 h-4 text-[#E65100]" />
                <span className="font-mono">{new Date(s.starts_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-[#63534E]">
                <Users className="w-4 h-4" />
                <span>{s.attendees_count || 0} / {s.capacity} seats</span>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div className="font-mono text-2xl font-semibold text-[#E65100]">${s.price_usd.toFixed(0)}</div>
                <Dialog onOpenChange={(o) => o && setPicked(s)}>
                  <DialogTrigger asChild>
                    <Button data-testid={`book-satsang-${s.id}`} className="bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-10 text-sm">Reserve seat</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#FFFDF9] border-[#D4AF37]/40 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-xl text-[#4A0E17]">Reserve · {picked?.title}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                      <Input data-testid="satsang-booker-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                      <Input data-testid="satsang-booker-email" type="email" placeholder="Email (meeting link sent here)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                      <Button data-testid="satsang-confirm" disabled={paying} onClick={book} className="w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-12">
                        {paying ? "Redirecting…" : `Pay $${picked?.price_usd?.toFixed(0) || "0"} & reserve`}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
